import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "../../annotations";
import { Page, ContentPage, CTA, Media } from "./dtos";
import { CancelRequestOptions, ExtraFieldsParam } from ".";

const CONTENT_PAGE_SERVICE = `${Constants.API_URL}/bedrock/content_pages`;

export type GetContentPagesFilters = {
  search?: string;
  playerId?: number | number[] | null;
  superPageId?: number | number[] | null;
  segmentationId?: number | number[];
  videoProjectId?: number;
  myPages?: boolean;
};

export type GetContentPagesParams = GetContentPagesFilters & {
  page?: number;
  perPage?: number;
  sort?: "created_date" | "updated_date";
  order?: "asc" | "desc";
  extraFields?: ExtraFieldsParam;
};

export type CreateContentPageRequest = {
  title: string;
  callToAction?: CTA;
  playerId?: number;
  coachId: number;
  superPageId?: number;
  themeId?: number;
  originalMedia: Media[];
};

export interface CreateContentPageFromFormRequest extends CreateContentPageRequest {
  formRequestId: number;
}

export interface CreateContentPageFromReview extends CreateContentPageRequest {
  reviewId: number;
}

export type UpdateContentPageRequest = {
  title?: string;
  callToAction?: CTA | null;
  themeId?: number | null;
  videoProjectId?: number | null;
  originalMedia?: Media[];
  markAsShared?: boolean;
};

const defaultExtraFields: ExtraFieldsParam = {
  processingSteps: true,
  superPage: ["contentPageTheme", "templateContentPage", "videoScript", "categories"],
  superPageAssignment: true,
};

export class ContentPageServices {
  @scCatchServiceException()
  static async createContentPage(
    request: CreateContentPageRequest,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<ContentPage> {
    const { data } = await axios.post<ContentPage>(CONTENT_PAGE_SERVICE, request, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async createContentPageFromFormRequest(
    request: CreateContentPageFromFormRequest,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<ContentPage> {
    const { data } = await axios.post<ContentPage>(`${CONTENT_PAGE_SERVICE}/create_from_form_request`, request, {
      params: { extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async createContentPageFromReview(
    request: CreateContentPageFromReview,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<ContentPage> {
    const { data } = await axios.post<ContentPage>(`${CONTENT_PAGE_SERVICE}/create_from_review`, request, {
      params: { extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async getContentPages(
    params: GetContentPagesParams = {},
    options?: CancelRequestOptions
  ): Promise<Page<ContentPage>> {
    const effectiveParams = { ...params } as any;
    if (effectiveParams.playerId === -1) effectiveParams.playerId = "";
    if (effectiveParams.superPageId === -1) effectiveParams.superPageId = "";

    const { data } = await axios.get<Page<ContentPage>>(CONTENT_PAGE_SERVICE, { params: effectiveParams, ...options });
    return data;
  }

  @scCatchServiceException()
  static async getContentPage(
    pageId: number,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<ContentPage> {
    const { data } = await axios.get<ContentPage>(`${CONTENT_PAGE_SERVICE}/${pageId}`, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async updateContentPage(
    pageId: number,
    request: UpdateContentPageRequest,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<ContentPage> {
    const { data } = await axios.patch<ContentPage>(`${CONTENT_PAGE_SERVICE}/${pageId}`, request, {
      params: { extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async archiveContentPage(pageId: number): Promise<void> {
    await axios.delete<void>(`${CONTENT_PAGE_SERVICE}/${pageId}`);
  }
}
